/**
 * The commentary a duel is watched with.
 *
 * A duel freezes both players for up to DUEL_DECIDE_TICKS — ten seconds in the middle of an
 * action game — and for most of that the screen had nothing to say beyond "pick one". This is
 * what fills it: one silly line per duel, drawn under the buttons and gone when the duel is.
 *
 * ## Seeded off the duel, never rolled
 *
 * `pickDuelLine` is pure and takes the duel's id, which is `roomNameFor`'s arrangement and for
 * `roomNameFor`'s reason: the same duel reads the same line on every render, so the overlay can
 * call it straight out of the render body with no state, no effect and nothing to keep in step.
 * A `Math.random()` here would re-roll on every re-render — and the overlay re-renders whenever
 * either side locks — which would make the line flicker exactly when the player is reading it.
 *
 * It is also why nothing here touches the room's seeded stream: this is browser text, the
 * simulation never sees it, and a line is not allowed to consume a draw (CLAUDE.md, "Nothing
 * here draws from the room's stream").
 */

/**
 * The pool. Family-friendly, about the moment rather than about the opponent, and short enough
 * to be read in a glance by somebody who is also counting down a clock.
 *
 * They are deliberately about *this game* — dots, blobs, spikes, mass — rather than generic
 * rock-paper-scissors jokes, because the line is on screen at the one moment the arena has
 * stopped and the player is looking straight at it.
 */
export const DUEL_LINES: readonly string[] = [
  'Both of you have stopped. This is now a staring contest.',
  'Somewhere, a pellet is breathing a sigh of relief.',
  'Whoever blinks first still has to pick something.',
  'The dots have gone very quiet.',
  'Rock beats scissors. Everybody knows this. Everybody forgets this.',
  'Two blobs enter. One blob leaves slightly larger.',
  'This is the most thinking anyone has done all match.',
  'Your opponent is definitely overthinking it too.',
  'Ancient rules. Round bodies. No takebacks.',
  'Statistically, somebody here is about to pick rock.',
  'The pellets are not going anywhere. Probably.',
  'Choose wisely. Or quickly. Ideally both.',
  'Nobody has ever regretted paper. Except everybody.',
  'Two evenly matched circles walk into a bar.',
  'A wise blob once said: always scissors. That blob was eaten.',
  'No mass is moving. The tension is enormous.',
  'Frozen solid, and it is not even cold out.',
  'You could pick rock. They know you could pick rock.',
  'The spikes are watching. The spikes are always watching.',
  'Somebody is about to feel very clever indeed.',
  'Deep breaths. It is only your entire body mass.',
  'Legend says the first to hesitate loses. Legend is unreliable.',
  'Three options. One of them is right. Good luck.',
  'Steering will not help you here.',
  'This duel is being judged by absolutely nobody.',
  'Pick what they would never pick. They are doing the same.',
  'The arena is carrying on without you. Rude.',
  'You are both very round and very stuck.',
  'Fortune favours the blob who commits.',
  'Whatever happens next, blame the pellets.',
];

/**
 * FNV-1a, 32-bit — the same small hash `roomNameFor` seeds a room's name with.
 *
 * Copied rather than imported: that one is private to `@chompdome/shared`'s room names, and
 * widening a shared package's surface so a browser can pick a joke is the wrong trade. Not a
 * security primitive, and nothing depends on the exact mapping — only on it being stable.
 */
function hashSeed(seed: string): number {
  let hash = 0x811c_9dc5;
  for (let index = 0; index < seed.length; index += 1) {
    hash ^= seed.charCodeAt(index);
    // The FNV prime, by shifts, because `hash * 16777619` loses the low bits to float precision.
    hash = (hash + ((hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24))) >>> 0;
  }
  return hash >>> 0;
}

/**
 * The line this duel is commented with. Same duel, same line, for as long as the duel lasts.
 *
 * `lines` is a parameter so a test can pin the mapping without pinning the pool's contents, and
 * an empty pool answers an empty string rather than throwing — a joke must never be able to take
 * the overlay down over the one moment a player cannot move.
 */
export function pickDuelLine(duelId: string, lines: readonly string[] = DUEL_LINES): string {
  if (lines.length === 0) return '';
  return lines[hashSeed(duelId) % lines.length];
}
